import * as React from 'react';
import propz from 'propz';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Loader } from 'Src/components/Loader/Loader';
import { ClubProgrammeActivationPreviewResponse } from 'Src/helpers/service/admin/clubProgrammes';

type Props = {
  isOpen: boolean;
  isLoading: boolean;
  isActivating: boolean;
  data: ClubProgrammeActivationPreviewResponse | null;
  onClose: () => void;
  onActivate: () => void;
};

export const ClubProgrammeActivationPreviewModal: React.FC<Props> = ({
  isOpen,
  isLoading,
  isActivating,
  data,
  onClose,
  onActivate
}) => {
  if (!isOpen) return null;

  const DAY_ORDER = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7
  };

  const sortedClubs = data
    ? [...(data.clubs || [])].sort((a, b) => {
        const dayA = String(propz.get(a, ['schedule', 'days', 0], '') || '').toUpperCase();
        const dayB = String(propz.get(b, ['schedule', 'days', 0], '') || '').toUpperCase();
        return (DAY_ORDER[dayA] || 99) - (DAY_ORDER[dayB] || 99);
      })
    : [];

  return (
    <SimpleModal
      isOpen={true}
      title="Activate programme clubs"
      showFooter={false}
      onCloseClick={onClose}
      customClass="pbBookingModal pbBookingActivationModal"
      dialogStyle={{ maxWidth: 900 }}
    >
      <div className="p-2">
        {isLoading ? (
          <div className="py-4 text-center">
            <Loader />
          </div>
        ) : !data ? (
          <div className="alert alert-light border mb-0">No activation preview data.</div>
        ) : (
          <>
            <div
              className="border rounded p-3 mb-3"
              style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #fffdf3 100%)' }}
            >
              <div className="h5 mb-2">{String(propz.get(data, ['programme', 'name'], 'Programme'))}</div>

              <div className="text-muted mb-2">
                You are about to activate all clubs in this programme and create live event chains. Please review the
                summary below before continuing.
              </div>

              <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                <span className="badge badge-light border">
                  Clubs: <b>{Number(propz.get(data, ['totals', 'clubsCount'], 0) || 0)}</b>
                </span>

                <span className="badge badge-light border">
                  Confirmed to activate:{' '}
                  <b>{Number(propz.get(data, ['totals', 'confirmedToActivateCount'], 0) || 0)}</b>
                </span>

                <span className="badge badge-light border">
                  Waiting list: <b>{Number(propz.get(data, ['totals', 'waitingListCount'], 0) || 0)}</b>
                </span>

                <span className="badge badge-primary">Activation preview</span>
              </div>
            </div>

            <div className="d-flex flex-column" style={{ gap: 12 }}>
              {sortedClubs.map((club: any) => {
                const clubName = String(propz.get(club, ['clubName'], 'Club'));
                const maxParticipants =
                  typeof propz.get(club, ['maxParticipants']) === 'number'
                    ? Number(propz.get(club, ['maxParticipants']))
                    : null;

                const confirmedToActivateCount = Number(propz.get(club, ['confirmedToActivateCount'], 0) || 0);
                const waitingListCount = Number(propz.get(club, ['waitingListCount'], 0) || 0);
                const projectedFilledCount = Number(propz.get(club, ['projectedFilledCount'], 0) || 0);

                const freePlacesAfterActivation =
                  typeof propz.get(club, ['freePlacesAfterActivation']) === 'number'
                    ? Number(propz.get(club, ['freePlacesAfterActivation']))
                    : null;

                const warnings: string[] = Array.isArray(propz.get(club, ['warnings']))
                  ? propz.get(club, ['warnings'])
                  : [];

                const hasFreePlacesWarning = maxParticipants !== null && projectedFilledCount < maxParticipants;

                const isOverCapacity = maxParticipants !== null && projectedFilledCount > maxParticipants;

                return (
                  <div
                    key={String(propz.get(club, ['clubId'], ''))}
                    className="border rounded p-3"
                    style={{
                      background: '#fff',
                      borderColor: 'rgba(0,0,0,0.1)'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
                      <div>
                        <div className="font-weight-bold">{clubName}</div>
                        <div className="small text-muted">Status: {String(propz.get(club, ['status'], ''))}</div>
                      </div>

                      <div>
                        <span className="badge badge-light border">Review</span>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap mt-3" style={{ gap: 10 }}>
                      <span className="badge badge-light border">
                        Places: <b>{maxParticipants === null ? 'Unlimited' : maxParticipants}</b>
                      </span>

                      <span className="badge badge-light border">
                        Confirmed to activate: <b>{confirmedToActivateCount}</b>
                      </span>

                      <span className="badge badge-light border">
                        Waiting list: <b>{waitingListCount}</b>
                      </span>

                      <span className="badge badge-light border">
                        Filled after activation: <b>{projectedFilledCount}</b>
                      </span>

                      <span className="badge badge-light border">
                        Free places after activation:{' '}
                        <b>{freePlacesAfterActivation === null ? 'Unlimited' : freePlacesAfterActivation}</b>
                      </span>
                    </div>

                    {hasFreePlacesWarning ? (
                      <div className="alert alert-warning mt-3 mb-0">
                        This club will still have free places after activation. Please check whether this is expected
                        before continuing.
                      </div>
                    ) : null}

                    {isOverCapacity ? (
                      <div className="alert alert-danger mt-3 mb-0">
                        This club is over capacity after activation preview.
                      </div>
                    ) : null}

                    {warnings.length ? (
                      <div className="mt-3">
                        {warnings.map((warning, idx) => (
                          <div
                            key={`${String(propz.get(club, ['clubId'], ''))}-warn-${idx}`}
                            className="alert alert-warning mb-2"
                          >
                            {String(warning)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="d-flex justify-content-end mt-3" style={{ gap: 10 }}>
              <button type="button" className="btn btn-outline-secondary" disabled={isActivating} onClick={onClose}>
                Cancel
              </button>

              <button type="button" className="btn btn-primary" disabled={isActivating} onClick={onActivate}>
                {isActivating ? 'Activating...' : 'Activate clubs'}
              </button>
            </div>
          </>
        )}
      </div>
    </SimpleModal>
  );
};

ClubProgrammeActivationPreviewModal.displayName = 'ClubProgrammeActivationPreviewModal';
